import React, { forwardRef, useState } from 'react';
import { useLanguage } from '@/contexts';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'onChange'> {
  label: string;
  options: SelectOption[];
  error?: string;
  required?: boolean;
  onChange: (value: string) => void;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  ({ label, options, error, required, onChange, className = '', ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false);
    const { t } = useLanguage();

    const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
      onChange(e.target.value);
    };

    return (
      <div className="relative">
        <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
        <select
          ref={ref}
          onChange={handleChange}
          required={required}
          className={`
            w-full rounded-md border appearance-none
            ${className || 'px-2 py-1.5 sm:px-3 sm:py-2 md:px-4 md:py-3 text-xs sm:text-sm md:text-base'}
            ${error ? 'border-red-500 dark:border-red-400' : 'border-gray-300 dark:border-slate-600'}
            ${isFocused ? 'border-brand-electric-blue dark:border-blue-400' : ''}
            bg-white dark:bg-slate-800 text-gray-900 dark:text-slate-100
            focus:outline-none focus:ring-1 focus:ring-brand-electric-blue dark:focus:ring-blue-400
            transition-colors duration-200
          `}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          {...props}
        >
          <option value={""} disabled>
            {label || t('selectAnOption')}
          </option>
          {options.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>

        <div className="absolute inset-y-0 right-0 flex items-center px-2 sm:px-3 pointer-events-none">
          <svg
            className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 text-gray-400 dark:text-slate-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
        
        {error && <p className="mt-1 text-sm text-red-500 dark:text-red-400">{error}</p>}
      </div>
    );
  }
);

Select.displayName = 'Select';
